import { createContext, useContext, useState } from "react";
import { getFileIconSrc, handleFileIconError } from "../../utils/fileIcon";
import { FolderIcon, RecycleIcon, TrashIcon } from "lucide-react";

import {
  Button,
  Flyout,
  FlyoutHeader,
  FlyoutTitle,
  styled,
  useTheme,
} from "@monolith-forensics/monolith-ui";

import { FlyoutDetailsTab } from "./FlyoutDetailsTab";
import { Activity } from "./Activity";
import { useCaseFiles } from "../../Hooks";
import { useGetFiles } from "../../files.query";
import { File } from "../../files.types";
import { DeleteCaseFileModal } from "../../Modals";
import { ManagedActionHint } from "@/Features/EvidenceFiles/ManagedActionHint";
import {
  isManagedFolder,
  managedFolderMessage,
} from "@/Features/EvidenceFiles/evidence-files.utils";

const tabDefs = [
  {
    label: "Details",
    value: "details",
  },
  {
    label: "Activity",
    value: "activity",
  },
];

const StyledTabPanel = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
`;

const TabsContext = createContext<{
  activeTab: string;
  setActiveTab: (tab: string) => void;
}>({
  activeTab: "details",
  setActiveTab: () => {},
});

const TabPanel: React.FC<{ children: React.ReactNode; value: string }> = ({
  children,
  value,
}) => {
  const { activeTab } = useContext(TabsContext);

  if (activeTab !== value) return null;
  return <StyledTabPanel>{children}</StyledTabPanel>;
};

const StyledTabs = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;

  .tabs-list {
    user-select: none;
    cursor: pointer;
    display: flex;
    flex-direction: row;
    color: ${({ theme }) => theme.palette.text.secondary};
    border-bottom: 1px solid ${({ theme }) => theme.palette.divider};
  }

  .tab {
    padding-right: 10px;
    padding-left: 10px;
    padding-top: 5px;
    padding-bottom: 5px;
    border-bottom: 1px solid transparent;
  }

  .tab:hover {
    background-color: ${({ theme }) => theme.palette.action.hover};
  }

  .tab[data-active="true"] {
    border-bottom-color: ${({ theme }) => theme.palette.primary.main};
    color: ${({ theme }) => theme.palette.primary.main};
  }

  .tab-content {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    height: 0px;
  }
`;

const Tabs: React.FC<{
  children: React.ReactNode;
  defaultValue: string;
  onChange: (tab: string) => void;
  tabs?: typeof tabDefs;
}> = ({ children, defaultValue, onChange, tabs = tabDefs }) => {
  if (!Array.isArray(children)) children = [children]; // Ensure children is an array
  const [activeTab, setActiveTab] = useState(defaultValue || "details");

  const handleChange = (tab: string) => {
    setActiveTab(tab);
    onChange?.(tab);
  };

  return (
    <TabsContext.Provider value={{ activeTab, setActiveTab }}>
      <StyledTabs>
        <div className="tabs-list">
          {tabs.map((tab) => (
            <div
              className={`tab`}
              data-active={activeTab === tab.value}
              onClick={() => handleChange(tab.value)}
              key={tab.value}
            >
              {tab.label}
            </div>
          ))}
        </div>
        <div className="tab-content">{children}</div>
      </StyledTabs>
    </TabsContext.Provider>
  );
};

export const CaseFileFlyout: React.FC<{
  open: boolean;
  onClose: () => void;
  defaultData: Partial<File>;
  /** Also gates the Delete button: omit it for a user who can't delete. */
  onDelete?: () => void;
}> = ({ open = false, onClose, defaultData, onDelete }) => {
  const { case_uuid, surface, api } = useCaseFiles();
  // Asset attachments (ENG-2291) have no case: no Activity tab, which reads
  // the case file logs.
  const caseless = surface === "asset-attachments";

  const theme = useTheme();
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const { data, isLoading, refetch } = useGetFiles(
    {
      case_uuid,
      uuid: defaultData.uuid,
    },
    {
      api,
      enabled: !!defaultData.uuid,
      initialData: {
        data: defaultData ? [defaultData as File] : [],
        path: [],
        nextPage: null,
        page: 1,
      },
    }
  );

  const itemData = data?.data?.[0];

  const isFolder = itemData?.is_folder;

  const fileIconSrc = isFolder
    ? undefined
    : getFileIconSrc(itemData?.name || "text.txt");

  return (
    <Flyout open={open} onClose={() => onClose?.()}>
      {isLoading && <div>Loading...</div>}
      {!isLoading && (
        <>
          <FlyoutHeader>
            <FlyoutTitle>
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  gap: 10,
                  alignItems: "center",
                }}
              >
                {isFolder ? (
                  <FolderIcon size={25} color="#c98200" />
                ) : (
                  <img
                    src={fileIconSrc}
                    onError={handleFileIconError}
                    alt="icon"
                    style={{ height: "25px" }}
                  />
                )}
                <div>{itemData?.name}</div>
              </div>
            </FlyoutTitle>
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                gap: 10,
                alignItems: "center",
                color: theme.palette.text.secondary,
                fontWeight: 500,
                marginBottom: 5,
              }}
            >
              <div>
                {caseless
                  ? "Attachment"
                  : isFolder
                  ? "Case Folder"
                  : "Case File"}
              </div>
              {onDelete && (
                <Button
                  size="xxs"
                  variant="text"
                  leftSection={<TrashIcon size={14} />}
                  color="error"
                  onClick={() => setShowDeleteModal(true)}
                  // The Files API refuses deleting the managed folders (ENG-2133).
                  disabled={isManagedFolder(itemData)}
                >
                  Delete
                </Button>
              )}
              {isManagedFolder(itemData) && (
                <ManagedActionHint
                  message={managedFolderMessage("deleted")}
                  data-testid="case-file-flyout-delete-managed-hint"
                />
              )}
              {itemData?.status === "archived" && (
                <Button
                  size="xxs"
                  variant="text"
                  leftSection={<RecycleIcon size={14} />}
                  color="green"
                  onClick={() => {
                    // TODO: Create Restore Funtion
                  }}
                >
                  Restore File
                </Button>
              )}
            </div>
            {showDeleteModal && itemData && (
              <DeleteCaseFileModal
                show={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                onCancel={() => setShowDeleteModal(false)}
                onSubmit={() => onDelete?.()}
                defaultFormData={itemData}
              />
            )}
          </FlyoutHeader>
          <Tabs
            defaultValue="details"
            onChange={() => refetch()}
            tabs={
              caseless ? tabDefs.filter((t) => t.value !== "activity") : tabDefs
            }
          >
            <TabPanel value="details">
              {itemData && (
                <FlyoutDetailsTab
                  defaultData={itemData}
                  onChange={() => refetch()}
                />
              )}
            </TabPanel>
            {!caseless && (
              <TabPanel value="activity">
                {itemData && <Activity defaultData={itemData} />}
              </TabPanel>
            )}
          </Tabs>
        </>
      )}
    </Flyout>
  );
};
